import L from 'leaflet'
import 'leaflet-polylinedecorator'
import { isArray } from '@/global/utils/utils'
import { toPathLatLngs } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/taskData'
import {
  PATH_TYPE,
  STREET_PATH_COLOR
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

export const PATH_WEIGHT = 5
export const POLYGON_FILL_OPACITY = 0.3

/**
 * How a work order's progress is drawn over the street itself.
 *
 * The three states a stretch can be in: finished, started but short of the passes it asks
 * for, and untouched. Together they cover the whole street, so they are drawn *instead of*
 * the street's own line rather than over it - as an overlay the wider line underneath
 * caught the cursor along its edges, and the hover flickered between the street's name and
 * the stretch under the pointer.
 */
export const COVERAGE_STATE = {
  covered: 'covered',
  partial: 'partial',
  uncovered: 'uncovered'
}

export const COVERAGE_COLOR = {
  [COVERAGE_STATE.covered]: '#16A34A',
  // Orange rather than the reported-irregularity yellow: the two sit on top of each other
  // on this map, and a half-driven stretch is not the same thing as "look at this". It is
  // also the amber the rest of the app means "started, not finished" by - the vehicle
  // markers' CONTACT/IDLING, among others - and it holds its own against a pale basemap,
  // which a yellow line never quite did.
  [COVERAGE_STATE.partial]: '#F59E0B',
  [COVERAGE_STATE.uncovered]: '#DC2626'
}

export const COVERAGE_WEIGHT = PATH_WEIGHT

// Started and not finished, said in the shape of the line as well as in its colour -
// colour alone is not something to make a reading depend on.
const PARTIAL_DASH_ARRAY = '6 6'

// The direction arrows along a route, matching what MapBase draws on its own polylines.
const ARROW_OFFSET = 25
const ARROW_REPEAT = 50
const ARROW_SIZE = 12
const ARROW_HEAD_ANGLE = 30

/**
 * How a task street is drawn on a map, in one place.
 *
 * Every screen that shows them - the listing's side map, the task's own route tab, a
 * request's and a work order's map - draws them the same way: a route for a polyline,
 * a filled shape for a polygon, both in the colour of the task they belong to. Keeping
 * that here is what makes them agree.
 */

export function isPolygonStreet (street) {
  return street?.path_type?.name === PATH_TYPE.polygon
}

/**
 * A circle is only drawable with a radius: the shape is its centre plus that, and a row
 * missing one is left to whatever its points make it.
 */
export function isCircleStreet (street) {
  return street?.path_type?.name === PATH_TYPE.circle && Number(street?.radius) > 0
}

export function isAreaStreet (street) {
  return isPolygonStreet(street) || isCircleStreet(street)
}

/**
 * The colour the street is drawn in. It belongs to the task rather than to the street,
 * and the endpoints report it on every street - a task without one falls back to the
 * winter service green.
 */
export function streetColor (street) {
  return street?.color || STREET_PATH_COLOR
}

/**
 * The stretches a work order cut a street into, with their points decoded. The backend
 * reports each as its own encoded polyline, so they are kept apart rather than flattened
 * the way a street's own parts are - each is drawn in the colour of how it went.
 */
export function toCoverageSegments (street) {
  return (isArray(street?.coverage?.segments) ? street.coverage.segments : [])
    .map(segment => ({
      ...segment,
      points: toPathLatLngs(segment?.polyline)
    }))
    .filter(segment => segment.points.length >= 2)
}

function streetTooltip (street) {
  return [street?.street_name, street?.street_district].filter(Boolean).join(' - ')
}

/**
 * The streets as MapBase's `generatePolylines` takes them.
 *
 * @param {Array} streets streets as the backend reports them
 * @param {Object} [options]
 * @param {Object} [options.renderer] leaflet renderer to draw with, e.g. a shared canvas
 * @param {String} [options.tooltipClass] class of the popup a shape opens on click
 * @param {Function} [options.onStreetClick] called with the street the shape stands for
 */
export function buildStreetShapes (streets, options = {}) {
  const {
    renderer = null,
    tooltipClass = '',
    onStreetClick = null,
    // What a coverage stretch says on hover. The wording belongs to the screen showing it,
    // not here; without one a stretch says what its street says.
    coverageTooltip = null
  } = options

  const polylines = []
  const circles = []
  // Held apart and appended last: they are drawn in the order they are handed over, and a
  // stretch has to end up over every street rather than only over its own.
  const covered = []

  ;(isArray(streets) ? streets : []).forEach(street => {
    const isArea = isAreaStreet(street)
    const color = streetColor(street)
    const points = toPathLatLngs(street?.path)
    const segments = toCoverageSegments(street)

    const shared = {
      options: {
        id: `task-street-${street?.id}`,
        ...(renderer ? { renderer } : {}),
        color,
        weight: PATH_WEIGHT,
        fill: isArea,
        fillColor: color,
        fillOpacity: isArea ? POLYGON_FILL_OPACITY : 0
      },
      popup: {
        popupData: () => streetTooltip(street),
        popupOptions: {
          className: `polyline-tooltip ${tooltipClass}`.trim()
        }
      },
      ...(onStreetClick ? { polylineClick: () => onStreetClick(street) } : {})
    }

    segments.forEach((segment, index) => {
      covered.push({
        ...shared,
        options: {
          ...shared.options,
          id: `task-street-${street?.id}-coverage-${index}`,
          color: COVERAGE_COLOR[segment.state] || COVERAGE_COLOR[COVERAGE_STATE.uncovered],
          weight: COVERAGE_WEIGHT,
          fill: false,
          fillOpacity: 0,
          ...(segment.state === COVERAGE_STATE.partial ? { dashArray: PARTIAL_DASH_ARRAY } : {})
        },
        popup: {
          ...shared.popup,
          popupData: () => coverageTooltip
            ? coverageTooltip(street, segment)
            : streetTooltip(street)
        },
        coordinates: segment.points
      })
    })

    if (isCircleStreet(street)) {
      // Its single point is the centre; without one there is nothing to draw around.
      if (!points.length) {
        return
      }

      circles.push({
        ...shared,
        coordinates: points[0],
        radius: Number(street.radius)
      })

      return
    }

    // The stretches above already are the street, end to end and in the colour of how each
    // of them went. Drawing its own line as well would only put a second, differently
    // coloured copy of it underneath.
    if (points.length < 2 || segments.length) {
      return
    }

    polylines.push({
      ...shared,
      // A polygon is drawn as a closed, filled shape; leaflet's own Polyline layer
      // takes both, so routes and areas can be handed over in one call and fitted to
      // the same bounds.
      coordinates: isPolygonStreet(street) ? points.concat([points[0]]) : points
    })
  })

  return {
    polylines: polylines.concat(covered),
    circles
  }
}

/**
 * The arrows along a route, saying which way it is driven. The same ones MapBase puts
 * on the polylines it draws itself, so a route looks the same whichever map shows it.
 */
function createDirectionArrows (points, renderer) {
  return L.polylineDecorator(points, {
    patterns: [
      {
        offset: ARROW_OFFSET,
        repeat: ARROW_REPEAT,
        symbol: L.Symbol.arrowHead({
          pixelSize: ARROW_SIZE,
          headAngle: ARROW_HEAD_ANGLE,
          pathOptions: {
            ...(renderer ? { renderer } : {}),
            color: '#FFF',
            weight: 0,
            fillOpacity: 0.5,
            // A symbol sitting over a marker swallows that marker's mouse events
            // unless it takes none of its own.
            interactive: false
          }
        })
      }
    ]
  })
}

/**
 * One street as a leaflet layer, for the maps that keep their own layers rather than
 * handing the whole set to MapBase on every change. Answers null for a street with
 * nothing to draw.
 *
 * A route comes back as its line and its direction arrows together, so both are added
 * and dropped as one.
 */
/**
 * The popup and the click a street's shape carries, whichever shape it is.
 */
function withStreetBindings (layer, street, { tooltipClass = '', onStreetClick = null } = {}) {
  const tooltip = streetTooltip(street)

  if (tooltip) {
    layer.bindPopup(tooltip, {
      className: `polyline-tooltip ${tooltipClass}`.trim()
    })
  }

  if (onStreetClick) {
    layer.on('click', () => onStreetClick(street))
  }

  return layer
}

export function createStreetLayer (street, options = {}) {
  const {
    renderer = null,
    tooltipClass = '',
    onStreetClick = null,
    // Arrows say which way a route is driven, which is worth seeing while one task is
    // being looked at. A map showing the routes of a whole page of tasks at once is
    // not that map: there the arrows are noise on top of noise.
    withArrows = true
  } = options

  const points = toPathLatLngs(street?.path)
  const color = streetColor(street)
  const bindings = { tooltipClass, onStreetClick }
  const rendererOption = renderer ? { renderer } : {}

  // Its single point is the centre, and the radius is the rest of it.
  if (isCircleStreet(street)) {
    if (!points.length) {
      return null
    }

    return withStreetBindings(
      L.circle(points[0], {
        ...rendererOption,
        radius: Number(street.radius),
        color,
        weight: PATH_WEIGHT,
        fillColor: color,
        fillOpacity: POLYGON_FILL_OPACITY
      }),
      street,
      bindings
    )
  }

  if (points.length < 2) {
    return null
  }

  const isPolygon = isPolygonStreet(street)

  const layer = withStreetBindings(
    L.polyline(isPolygon ? points.concat([points[0]]) : points, {
      ...rendererOption,
      color,
      weight: PATH_WEIGHT,
      fill: isPolygon,
      fillColor: color,
      fillOpacity: isPolygon ? POLYGON_FILL_OPACITY : 0
    }),
    street,
    bindings
  )

  // An area has no direction to drive in, so it carries no arrows either way.
  if (isPolygon || !withArrows) {
    return layer
  }

  return L.featureGroup([layer, createDirectionArrows(points, renderer)])
}

/**
 * What a task's streets look like right now: their shapes and the colour they are
 * drawn in. A map that keeps its layers per task compares this to know whether the
 * ones it is holding are still the right ones - a task whose colour was just saved
 * keeps its id, but not its picture.
 */
export function streetsSignature (streets) {
  return (isArray(streets) ? streets : [])
    .map(street => [
      street?.id,
      streetColor(street),
      street?.path_type?.name,
      street?.radius,
      street?.updated_at,
      // What is drawn over it moves with the orders that are marked, not with the street.
      street?.coverage?.percent,
      (isArray(street?.coverage?.segments) ? street.coverage.segments : []).length
    ].join(':'))
    .join('|')
}

/**
 * The streets grouped by the task they belong to, which is how the listing's map keeps
 * them: ticking a task off has to add or drop that task's layers alone rather than
 * redraw a whole selection. Streets reported without a task - the details screens fetch
 * one task's own - fall into a single group of their own.
 */
export function groupStreetsByTask (streets) {
  return (isArray(streets) ? streets : []).reduce((byTask, street) => {
    const key = street?.task_id ?? 'unknown'
    const group = byTask.get(key)

    if (group) {
      group.push(street)
    }
    else {
      byTask.set(key, [street])
    }

    return byTask
  }, new Map())
}
